/**
 * Client copy of the entitlement catalog (server/src/lib/entitlements.ts).
 *
 * The workspaces cannot import each other, so this list is held in step by
 * server/test/orgModules.test.ts, which reads this file and compares keys,
 * kinds and `requires`. Add an entitlement in both places.
 *
 *   flag   boolean at organizations/{orgId}.modules.<key>; ON for a user
 *          when ANY of their orgs grants it, and every `requires` parent is on.
 *   limit  non-negative integer at organizations/{orgId}.limits.<key>; read
 *          for the ONE org a write names.
 */

export type EntitlementKind = 'flag' | 'limit';
export type EntitlementGroup = 'platform' | 'map' | 'events' | 'premise' | 'collaboration' | 'research' | 'custom_ui';

export interface Entitlement {
  key: string;
  label: string;
  group: EntitlementGroup;
  kind: EntitlementKind;
  requires?: readonly string[];
  description: string;
}

export const ENTITLEMENT_GROUPS: { id: EntitlementGroup; label: string }[] = [
  { id: 'platform', label: 'Platform' },
  { id: 'map', label: 'Map layers' },
  { id: 'events', label: 'Events' },
  { id: 'premise', label: 'Premise data' },
  { id: 'collaboration', label: 'Collaboration' },
  { id: 'research', label: 'Research' },
  { id: 'custom_ui', label: 'Custom UI' },
];

export const ENTITLEMENTS = [
  { key: 'alerts', label: 'Alerts', group: 'platform', kind: 'flag',
    description: 'The Alerts page (event cards, subscriptions, summaries).' },
  { key: 'infrastructure', label: 'Infrastructure', group: 'map', kind: 'flag',
    description: 'The map’s Infrastructure panel (cell towers and WiFi).' },
  { key: 'internal', label: 'Internal', group: 'platform', kind: 'flag',
    description: 'Culmen-internal tools not sold to customers.' },
  { key: 'monitor', label: 'Monitor', group: 'map', kind: 'flag',
    description: 'The map’s Monitor panel: upload and watch monitored locations.' },
  { key: 'monitors', label: 'Monitors purchased', group: 'map', kind: 'limit', requires: ['monitor'],
    description: 'How many monitored locations the organization may hold. Missing or zero means none may be added.' },
  { key: 'expressions', label: 'Expressions', group: 'platform', kind: 'flag',
    description: 'The Expressions page.' },
  { key: 'simulation', label: 'Simulation', group: 'platform', kind: 'flag',
    description: 'The Simulation page.' },
  { key: 'baseball_cards', label: 'Baseball Cards', group: 'platform', kind: 'flag',
    description: 'Contributor baseball cards (sys_admin only in practice).' },
  { key: 'geosignals', label: 'GeoSignals', group: 'map', kind: 'flag',
    description: 'The GeoSignals polygon layer, its info-panel tab and the GeoSignals report.' },
  { key: 'map_analyst', label: 'Mosaics Analyst (map)', group: 'map', kind: 'flag',
    description: 'The map’s Mosaics Agent chat panel and “Send to Agent” buttons. Mobile uses the same flag.' },
  { key: 'events', label: 'Events', group: 'events', kind: 'flag',
    description: 'The map’s Events panel and event blocks. Which sources show is set by the source flags below.' },
  { key: 'events_web', label: 'Web sources', group: 'events', kind: 'flag', requires: ['events'],
    description: 'Events from news and web sites (the bulk). Hourly and daily summaries need this.' },
  { key: 'events_premise', label: 'Premise sources', group: 'events', kind: 'flag', requires: ['events'],
    description: 'Events reported through Premise.' },
  { key: 'events_x', label: 'X / Twitter sources', group: 'events', kind: 'flag', requires: ['events'],
    description: 'Events sourced from X (Twitter).' },
  { key: 'events_reddit', label: 'Reddit sources', group: 'events', kind: 'flag', requires: ['events'],
    description: 'Events sourced from Reddit.' },
  { key: 'events_telegram', label: 'Telegram sources', group: 'events', kind: 'flag', requires: ['events'],
    description: 'Events sourced from Telegram channels.' },
  { key: 'premise_sentiment', label: 'Premise sentiment', group: 'premise', kind: 'flag',
    description: 'The “Premise Sentiment” layer source. Organization sentiment stays gated by project access.' },
  { key: 'premise_photos', label: 'Premise photos', group: 'premise', kind: 'flag',
    description: 'Premise photos in the info panel’s Photos tab and the Premise Data map layer.' },
  { key: 'byod', label: 'Bring your own data', group: 'premise', kind: 'flag',
    description: 'Uploaded documents, photos, videos and WiFi/cell CSVs under “My Data”.' },
  { key: 'comments', label: 'Comments', group: 'collaboration', kind: 'flag',
    description: 'Comment threads on polygons, POIs, infrastructure, media and sketchpads. Viewers still cannot post.' },
  { key: 'sketchpad', label: 'Sketchpad', group: 'collaboration', kind: 'flag',
    description: 'The map’s Collaboration panel: shared sketchpads.' },
  { key: 'research', label: 'Research', group: 'research', kind: 'flag',
    description: 'The Research workspace and its shared pickers.' },
  { key: 'research_report', label: 'Write a Report', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Write a Report workflow (research analyst, report mode).' },
  { key: 'research_collection', label: 'Collection plan', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Create a Collection Plan workflow.' },
  { key: 'research_analyze', label: 'Analyze data', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Analyze Data workflow.' },
  { key: 'research_network', label: 'Premise Network', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Premise Network analyst.' },
  { key: 'research_survey', label: 'Design a Survey', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The survey design analyst.' },
  { key: 'research_prediction_market', label: 'Prediction Markets', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The prediction-market analyst.' },
  { key: 'research_eyes_up', label: 'Eyes Up Report', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Eyes Up formatted report.' },
  { key: 'research_geosignals_report', label: 'GeoSignals Report', group: 'research', kind: 'flag', requires: ['research', 'geosignals'],
    description: 'The GeoSignals formatted report (needs the GeoSignals layer too).' },
  { key: 'research_map_creator', label: 'Map Creator', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Map Creator: the research analyst answering with GeoJSON on a map, in Research and as a dashboard block.' },
  { key: 'biosurveillance', label: 'Biosurveillance', group: 'research', kind: 'flag', requires: ['research'],
    description: 'The Biosurveillance workflow in Research.' },
  { key: 'custom_ui', label: 'Custom UI', group: 'custom_ui', kind: 'flag',
    description: 'The Custom UI builder and published dashboards.' },
  { key: 'custom_ui_review', label: 'Custom UI review', group: 'custom_ui', kind: 'flag', requires: ['custom_ui'],
    description: 'The event review workflow inside dashboards.' },
] as const satisfies readonly Entitlement[];

export type EntitlementKey = (typeof ENTITLEMENTS)[number]['key'];
type FlagOf<T> = T extends { kind: 'flag'; key: infer K } ? K : never;
type LimitOf<T> = T extends { kind: 'limit'; key: infer K } ? K : never;
export type FlagKey = FlagOf<(typeof ENTITLEMENTS)[number]>;
export type LimitKey = LimitOf<(typeof ENTITLEMENTS)[number]>;

export const FLAG_KEYS = ENTITLEMENTS.filter((e) => e.kind === 'flag').map((e) => e.key) as readonly FlagKey[];
export const LIMIT_KEYS = ENTITLEMENTS.filter((e) => e.kind === 'limit').map((e) => e.key) as readonly LimitKey[];

const BY_KEY: Record<string, Entitlement> = Object.fromEntries(ENTITLEMENTS.map((e) => [e.key, e]));

export function entitlementLabel(key: string): string {
  return BY_KEY[key]?.label ?? key;
}

/** Apply `requires`: a flag counts as on only when its parents are on too.
 *  Mirrors `effectiveFlags` on the server. */
export function effectiveFlags(raw: Record<string, boolean> | undefined | null): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const on = (key: string, depth = 0): boolean => {
    if (depth > 8) return false;
    if (raw?.[key] !== true) return false;
    const req = BY_KEY[key]?.requires ?? [];
    return req.every((p) => on(p, depth + 1));
  };
  for (const key of Object.keys(raw ?? {})) if (on(key)) out[key] = true;
  return out;
}

/** The flags, every one off: the AdminPanel's blank form. */
export function blankFlags(): Record<FlagKey, boolean> {
  return Object.fromEntries(FLAG_KEYS.map((k) => [k, false])) as Record<FlagKey, boolean>;
}

/** The limits, every one zero. */
export function blankLimits(): Record<LimitKey, number> {
  return Object.fromEntries(LIMIT_KEYS.map((k) => [k, 0])) as Record<LimitKey, number>;
}

/** The standard wording for a disabled entitlement, matching the server's
 *  403 body so a hidden control and a refused request read the same. */
export function disabledMessage(key: string): string {
  return `${entitlementLabel(key)} is not enabled for your organization`;
}
